import React, { useEffect, useMemo, useState } from 'react';

import styles from './styles.styl';
import cn from 'classnames/bind';
import DashboardHello from 'src/components/DashboardHello';
import SurveyList from 'src/components/SurveyList';
import { useDispatch, useSelector } from 'react-redux';
import {
  deleteSurveyLoadedSelector,
  deleteSurveyRejectedSelector,
} from 'src/redux/features/surveys/deleteSurvey/selectors';
import { State } from 'src/redux/reducer';
import { Informer } from '@consta/uikit/Informer';
import { deleteSurveyIdle } from 'src/redux/features/surveys/deleteSurvey/actions';
import { IconThumbUp } from '@consta/uikit/IconThumbUp';
import {
  changeStatusSurveyLoadedSelector,
  changeStatusSurveyRejectedSelector,
  changeStatusSurveySelector,
} from 'src/redux/features/surveys/changeSurveyStatus/selectors';
import {
  duplicateSurveySelector,
  duplicateSurveyLoadedSelector,
  duplicateSurveyRejectedSelector,
} from 'src/redux/features/surveys/duplicateSurvey/selectors';
import { changeStatusSurveyIdle } from 'src/redux/features/surveys/changeSurveyStatus/actions';
import { duplicateSurveyIdle } from 'src/redux/features/surveys/duplicateSurvey/actions';
import { IDuplicateSurveyData } from 'src/redux/features/surveys/duplicateSurvey/types';
import { PublishSurveyData } from 'src/redux/features/surveys/changeSurveyStatus/types';
import {
  InformerStatusTitleEnum,
  StatusInformerEnum,
  SurveyStatusEnum,
} from 'src/utils/enum';
import { StatusInformerType } from 'src/utils/types';
import { importSurveysRejectedSelector } from 'src/redux/features/surveys/importSurveys/selectors';
import {
  DashboardDeleteWarningTitle,
  DashboardDuplicatedWarningTitle,
  DashboardStatusWarningTitle,
  DashboardWarningTitle,
} from 'src/config/config';
import { importSurveysIdle } from 'src/redux/features/surveys/importSurveys/actions';

const cx = cn.bind(styles);

export interface IDashboardProps {}

const Dashboard: React.FC<IDashboardProps> = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [timerId, setTimerId] = useState<NodeJS.Timeout>();
  const [deletedName, setDeletedName] = useState('');

  const [statusInformer, setStatusInformer] = useState<StatusInformerType>(
    StatusInformerEnum.Success,
  );

  const dispatch = useDispatch();
  const deleted = useSelector((state: State) =>
    deleteSurveyLoadedSelector(state),
  );
  const deletedWarning = useSelector((state: State) =>
    deleteSurveyRejectedSelector(state),
  );
  const updatedStatus = useSelector((state: State) =>
    changeStatusSurveyLoadedSelector(state),
  );
  const updatedStatusWarning = useSelector((state: State) =>
    changeStatusSurveyRejectedSelector(state),
  );
  const duplicated = useSelector((state: State) =>
    duplicateSurveyLoadedSelector(state),
  );
  const duplicatedWarning = useSelector((state: State) =>
    duplicateSurveyRejectedSelector(state),
  );

  const importSurveyWarning = useSelector((state: State) =>
    importSurveysRejectedSelector(state),
  );

  const updatedSurvey: PublishSurveyData = useSelector((state: State) =>
    changeStatusSurveySelector(state),
  );
  const duplicatedSurvey: IDuplicateSurveyData = useSelector((state: State) =>
    duplicateSurveySelector(state),
  );

  const surveyName = useMemo(() => {
    if (duplicated) return duplicatedSurvey?.name;
    if (updatedStatus) return updatedSurvey?.name;
    if (deleted) return deletedName;
    if (duplicatedWarning) return DashboardDuplicatedWarningTitle;
    if (deletedWarning) return DashboardDeleteWarningTitle;
    if (updatedStatusWarning) return DashboardStatusWarningTitle;
    if (importSurveyWarning) return DashboardWarningTitle;
    return '';
  }, [
    duplicated,
    duplicatedSurvey,
    updatedStatus,
    updatedSurvey,
    deleted,
    deletedName,
    duplicatedWarning,
    importSurveyWarning,
    deletedWarning,
    updatedStatusWarning,
  ]);

  const informerText: string = useMemo(() => {
    if (duplicated) return InformerStatusTitleEnum.DUPLICATE_SUCCESS;
    if (deleted) return InformerStatusTitleEnum.DELETE_SUCCESS;
    if (updatedStatus) {
      if (updatedSurvey.status === SurveyStatusEnum.PUBLISHED)
        return InformerStatusTitleEnum.PUBLISH_SUCCESS;
      if (updatedSurvey.status === SurveyStatusEnum.ENDED)
        return InformerStatusTitleEnum.END_SUCCESS;
      return '';
    }
    if (duplicatedWarning) {
      return InformerStatusTitleEnum.PUBLISH_WARNING;
    }
    if (deletedWarning) {
      return InformerStatusTitleEnum.DELETE_WARNING;
    }
    if (updatedStatusWarning) {
      return InformerStatusTitleEnum.STATUS_WARNING;
    }
    if (importSurveyWarning) {
      return InformerStatusTitleEnum.IMPORT_SURVEY_WARNING;
    }
    return '';
  }, [
    duplicated,
    deleted,
    updatedStatus,
    updatedSurvey?.status,
    duplicatedWarning,
    deletedWarning,
    updatedStatusWarning,
    importSurveyWarning,
  ]);

  useEffect(() => {
    if (
      deleted ||
      updatedStatus ||
      duplicated ||
      duplicatedWarning ||
      deletedWarning ||
      updatedStatusWarning ||
      importSurveyWarning
    ) {
      if (deleted || updatedStatus || duplicated) {
        setStatusInformer(StatusInformerEnum.Success);
      } else if (
        importSurveyWarning ||
        duplicatedWarning ||
        deletedWarning ||
        updatedStatusWarning
      ) {
        setStatusInformer(StatusInformerEnum.Alert);
      }
      setTitle(surveyName);
      setText(informerText);
      setIsOpen(true);

      if (timerId) clearTimeout(timerId);
      setTimerId(
        setTimeout(() => {
          setIsOpen(false);
        }, 5000),
      );
    }
    if (deleted || deletedWarning) dispatch(deleteSurveyIdle());
    if (updatedStatus || updatedStatusWarning)
      dispatch(changeStatusSurveyIdle());
    if (duplicated || duplicatedWarning) dispatch(duplicateSurveyIdle());
    if (importSurveyWarning) dispatch(importSurveysIdle());
    return () => {
      clearTimeout(timerId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    deleted,
    updatedStatus,
    duplicated,
    duplicatedWarning,
    deletedWarning,
    updatedStatusWarning,
    importSurveyWarning,
  ]);

  return (
    <div className={cx('Dashboard')}>
      {isOpen && (
        <div className={cx('Dashboard__informer')}>
          <Informer
            status={statusInformer}
            size="s"
            title={title}
            label={text}
            onClick={() => {
              setIsOpen(false);
              if (timerId) clearTimeout(timerId);
            }}
            icon={
              statusInformer === StatusInformerEnum.Alert ? null : IconThumbUp
            }
          />
        </div>
      )}
      <div className={cx('Dashboard__hello')}>
        <DashboardHello />
      </div>
      <div>
        <SurveyList setDeletedName={setDeletedName} />
      </div>
    </div>
  );
};

export default Dashboard;
